import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonGroupVariants = cva("inline-flex items-center", {
  variants: {
    orientation: {
      horizontal: "flex-row",
      vertical: "flex-col",
    },
  },
  defaultVariants: {
    orientation: "horizontal",
  },
});

interface ButtonGroupProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof buttonGroupVariants> {
  asChild?: boolean;
}

const ButtonGroup = React.forwardRef<HTMLDivElement, ButtonGroupProps>(
  ({ className, orientation, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "div";
    return (
      <Comp
        ref={ref}
        role="group"
        className={cn(
          buttonGroupVariants({ orientation }),
          // First child: rounded-l only
          "[&>*:first-child]:rounded-l-md [&>*:first-child]:rounded-r-none",
          // Last child: rounded-r only
          "[&>*:last-child]:rounded-r-md [&>*:last-child]:rounded-l-none",
          // Middle children: no rounding
          "[&>*:not(:first-child):not(:last-child)]:rounded-none",
          // Remove negative margin for seamless connection
          "[&>*:not(:first-child)]:border-l-0",
          orientation === "vertical" &&
            "[&>*:first-child]:rounded-t-md [&>*:first-child]:rounded-b-none [&>*:last-child]:rounded-b-md [&>*:last-child]:rounded-t-none [&>*:not(:first-child):not(:last-child)]:rounded-none [&>*:not(:first-child)]:border-t-0 [&>*:not(:first-child)]:border-l [&>*:not(:first-child)]:-mt-px [&>*:not(:first-child)]:ml-0",
          className
        )}
        {...props}
      />
    );
  }
);
ButtonGroup.displayName = "ButtonGroup";

const ButtonGroupSeparator = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    orientation?: "horizontal" | "vertical";
  }
>(({ className, orientation = "vertical", ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "bg-border",
      orientation === "vertical" ? "w-px h-full" : "h-px w-full",
      className
    )}
    {...props}
  />
));
ButtonGroupSeparator.displayName = "ButtonGroupSeparator";

export { ButtonGroup, ButtonGroupSeparator };
